import React, { useMemo } from 'react';
import Tippy, { TippyProps } from '@tippyjs/react';
import { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core';

import type { WosIconProp } from 'types/common_types';
import Icon from 'components/shared/Icon';

export type ButtonType =
  | 'regular'
  | 'outline'
  | 'bare'
  | 'cornered'
  | 'icon'
  | 'confirm'
  | 'cancel'
  | 'link';

type HTMLButtonType = 'button' | 'submit' | 'reset';

export type ButtonAppearance = 'dark' | 'light' | 'themed' | 'primary';

type ButtonProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'type' | 'role' | 'onClick' | 'className'
> & {
  /** Allow button to follow theme, or lock to specific appearance */
  appearance?: ButtonAppearance;
  /** Determines structure & style of Button */
  type?: ButtonType;
  /** Text to use for screen reader access/tooltip */
  a11yText?: string;
  /**  */
  ariaControls?: React.AriaAttributes['aria-controls'];
  role?: React.AriaRole;
  className?: string;
  id?: string;
  disabled?: boolean;
  dataset?: { [key: string]: string };
  htmlType?: HTMLButtonType;
  /** ID of related form element, if using submit/reset htmlType */
  form?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Name of icon to include, if using icon button type */
  icon?: WosIconProp | IconProp;
  /** Size of icon if using icon button type */
  size?: SizeProp;
  tooltipProps?: TippyProps;
};

function Button({
  a11yText,
  ariaControls,
  children,
  icon,
  id,
  form,
  dataset,
  className = '',
  appearance = 'dark',
  disabled = false,
  size = '1x',
  type = 'regular',
  htmlType = 'button',
  onClick = () => {},
  // onKeyDown = () => {},
  tooltipProps = { placement: 'top' },
  role = 'button',
  ...rest
}: ButtonProps) {
  const dataAttributes = useMemo(() => {
    const dataAttributes: { [key: string]: string } = {};

    if (dataset) {
      if (Object.entries(dataset).length > 0) {
        Object.entries(dataset).forEach(([key, val]) => {
          dataAttributes[`data-${key}`] = val;
        });
      }
    }

    return dataAttributes;
  }, [dataset]);

  const iconComponent = useMemo(() => {
    if (type !== 'icon') return null;

    if (!icon) throw new Error('Icon Button requires a valid icon');

    return <Icon icon={icon} size={size} />;
  }, [type, icon, size]);

  const button = (
    <button
      disabled={disabled}
      id={id && id}
      className={`button ${type} ${className} ${appearance}`}
      type={htmlType}
      onClick={onClick}
      form={form}
      aria-label={a11yText}
      aria-controls={ariaControls}
      role={role}
      {...dataAttributes}
      {...rest}
    >
      {type === 'icon' && iconComponent}
      {children}
    </button>
  );

  if (type === 'icon') {
    if (disabled) {
      return (
        <Tippy {...tooltipProps} touch={false} content={a11yText}>
          <span className="disabled-wrapper">{button}</span>
        </Tippy>
      );
    }
    return (
      <Tippy {...tooltipProps} touch={false} content={a11yText}>
        {button}
      </Tippy>
    );
  }

  return button;
}

export default Button;
